import { Component, OnInit, Pipe, PipeTransform } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HrService } from '../../../services/human-resources/hr.service';
import { Document, DocumentStatus, DocumentType, Employee, CVAnalysisStatus } from '../../../models/hr-models';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Custom pipe to make URLs clickable
@Pipe({
  name: 'linky',
  standalone: true
})
export class LinkyPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) return '';
    
    // URL regex pattern
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    
    // Replace URLs with anchor tags
    const result = value.replace(urlPattern, url => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    
    // Return as safe HTML
    return this.sanitizer.bypassSecurityTrustHtml(result);
  }
}

// Custom pipe to filter objects by property
@Pipe({
  name: 'filter',
  standalone: true
})
export class FilterPipe implements PipeTransform {
  transform(items: any[], filter: any): any[] {
    if (!items || !filter) {
      return items;
    }
    
    return items.filter(item => {
      const notMatchingField = Object.keys(filter).find(key => {
        return item[key] !== filter[key];
      });
      return !notMatchingField; // true if all filter criteria match
    });
  }
}

@Component({
  selector: 'app-document-tracking',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, LinkyPipe, FilterPipe],
  templateUrl: './document-tracking.component.html',
  styleUrls: ['./document-tracking.component.scss']
})
export class DocumentTrackingComponent implements OnInit {
  documents$: Observable<Document[]>;
  employees$: Observable<Employee[]>;
  documentTypes$: Observable<DocumentType[]>;
  filteredDocuments$: Observable<Document[]>;
  documentForm: FormGroup;
  cvUploadForm: FormGroup;
  editingDocument: Document | null = null;
  selectedEmployee: number | null = null;
  selectedStatus: DocumentStatus | '' = '';
  selectedType: DocumentType | '' = '';
  searchTerm = '';
  showDocumentModal = false;
  showCVUploadModal = false;
  showCVSummaryModal = false;
  currentCVSummary: any = null;
  isUploading = false;
  uploadError = '';
  selectedFile: File | null = null;
  selectedDocumentFile: File | null = null;
  documentUploadError = '';
  selectedDocumentFiles: File[] = [];
  // Add console for template access
  console = console;

  documentStatuses = Object.values(DocumentStatus);
  cvAnalysisStatuses = Object.values(CVAnalysisStatus);

  constructor(
    private hrService: HrService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer
  ) {
    this.documents$ = this.hrService.documents$;
    this.employees$ = this.hrService.employees$;
    this.documentTypes$ = this.hrService.documentTypes$.pipe(
      map(types => types.filter(type => type.status === 1))
    );
    this.filteredDocuments$ = this.documents$;
    this.documentForm = this.fb.group({
      title: ['', Validators.required],
      employeeId: [null, Validators.required],
      documentType: ['', Validators.required],
      issueDate: [null, Validators.required],
      expiryDate: [null, Validators.required],
      documentUrl: [''],
      notes: ['']
    });

    this.cvUploadForm = this.fb.group({
      title: ['', Validators.required],
      employeeId: ['', Validators.required],
      issueDate: ['', Validators.required],
      cv_file: [null]
    });

    // Debug initial form state
    console.log('[Document Tracking] Initial CV Upload Form state:', {
      valid: this.cvUploadForm.valid,
      invalid: this.cvUploadForm.invalid
    });
  }

  ngOnInit(): void {
    this.hrService.refreshData();
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredDocuments$ = this.documents$.pipe(
      map(documents => {
        let filtered = documents;
        // Apply employee filter
        if (this.selectedEmployee) {
          filtered = filtered.filter(doc => doc.employeeId === this.selectedEmployee);
        }

        // Apply status filter
        if (this.selectedStatus) {
          filtered = filtered.filter(doc => doc.status === this.selectedStatus);
        }

        // Apply type filter
        if (this.selectedType) {
          filtered = filtered.filter(doc => doc.documentType === this.selectedType);
        }

        // Apply search term
        if (this.searchTerm.trim()) {
          const search = this.searchTerm.toLowerCase().trim();
          filtered = filtered.filter(doc =>
            doc.title.toLowerCase().includes(search) ||
            doc.notes?.toLowerCase().includes(search)
          );
        }
        
        return filtered;
      })
    );
  }

  resetFilters(): void {
    this.selectedEmployee = null;
    this.selectedStatus = '';
    this.selectedType = '';
    this.searchTerm = '';
    this.applyFilters();
  }

  addNewDocument(): void {
    this.editingDocument = null;
    this.documentForm.reset();
    this.selectedDocumentFile = null;
    this.documentUploadError = '';
    this.showDocumentModal = true;
  }

  editDocument(document: Document): void {
    console.log('document', document);
    this.editingDocument = document;
    this.selectedDocumentFile = null;
    this.documentUploadError = '';

    // Format date considering timezone
    const formatDateForInput = (dateString: string | Date) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.documentForm.setValue({
      title: document.title,
      employeeId: document.employeeId,
      documentType: document.documentType,
      issueDate: formatDateForInput(document.issueDate),
      expiryDate: formatDateForInput(document.expiryDate),
      documentUrl: document.file_path || '',
      notes: document.notes || ''
    });
    this.showDocumentModal = true;
  }

  saveDocument(): void {
    if (this.documentForm.invalid) {
      return;
    }
    const formValue = this.documentForm.value;

    // Determine document status based on expiry date
    const expiryDate = new Date(formValue.expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    let status: DocumentStatus;
    if (daysUntilExpiry < 0) {
      status = DocumentStatus.Expired;
    } else if (daysUntilExpiry <= 30) {
      status = DocumentStatus.Expiring;
    } else {
      status = DocumentStatus.Valid;
    }

    // Check if we're editing and if files have changed
    const hasNewFiles = this.selectedDocumentFiles && this.selectedDocumentFiles.length > 0;
    
    if (this.editingDocument && !hasNewFiles) {
      // Just update document metadata without file changes
      const document: Document = {
        id: this.editingDocument.id,
        employeeId: formValue.employeeId,
        title: formValue.title,
        documentType: formValue.documentType as DocumentType,
        issueDate: new Date(formValue.issueDate),
        expiryDate: expiryDate,
        status: status,
        documentUrl: formValue.documentUrl || this.editingDocument.documentUrl,
        notes: formValue.notes,
        notificationsSent: this.editingDocument.notificationsSent || [],
        file_name: this.editingDocument.file_name,
        file_path: this.editingDocument.file_path,
        file_type: this.editingDocument.file_type,
        file_size: this.editingDocument.file_size
      };

      this.isUploading = true;
      this.hrService.updateDocument(document).subscribe({
        next: (updatedDoc) => {
          this.isUploading = false;
          console.log('[Document Tracking] Document updated successfully:', updatedDoc);
          this.showDocumentModal = false;
          this.documentForm.reset();
          this.selectedDocumentFiles = [];
          this.hrService.getDocuments().subscribe();
        },
        error: (error) => {
          this.isUploading = false;
          console.error('[Document Tracking] Error updating document:', error);
          alert('Failed to update document. Please try again.');
        }
      });
      return;
    }
  
    // Handle file uploads (new document or updating with new files)
    if (hasNewFiles) {
      const formData = new FormData();
      
      if (this.selectedDocumentFiles.length > 1) {
        // Multiple files
        this.selectedDocumentFiles.forEach(file => {
          formData.append('document_files', file);
        });
        
        formData.append('employeeId', formValue.employeeId);
        formData.append('documentType', formValue.documentType);
        formData.append('title', formValue.title);
        
        // Format dates without timezone conversion
        const formatDate = (dateString: string) => {
          const date = new Date(dateString);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };
        
        formData.append('issueDate', formatDate(formValue.issueDate));
        formData.append('expiryDate', formatDate(formValue.expiryDate));
        
        if (formValue.notes) {
          formData.append('notes', formValue.notes);
        }

        if (this.editingDocument) {
          formData.append('id', this.editingDocument.id.toString());
        }
        
        this.isUploading = true;
        this.hrService.uploadMultipleDocuments(formData).subscribe({
          next: (response) => {
            this.isUploading = false;
            this.showDocumentModal = false;
            this.documentForm.reset();
            this.selectedDocumentFiles = [];
            this.hrService.getDocuments().subscribe();
            alert(`Successfully uploaded ${response.documents.length} document(s)`);
          },
          error: (error) => {
            this.isUploading = false;
            this.documentUploadError = error.error?.message || 'Failed to upload documents. Please try again.';
          }
        });
      } else {
        // Single file
        formData.append('document_file', this.selectedDocumentFiles[0]);
        formData.append('employeeId', formValue.employeeId);
        formData.append('title', formValue.title);
        formData.append('documentType', formValue.documentType);
        
        const issueDate = new Date(formValue.issueDate).toISOString().split('T')[0];
        const expiryDateStr = expiryDate.toISOString().split('T')[0];
        
        formData.append('issueDate', issueDate);
        formData.append('expiryDate', expiryDateStr);
        
        if (formValue.notes) {
          formData.append('notes', formValue.notes);
        }
        
        if (this.editingDocument) {
          formData.append('id', this.editingDocument.id.toString());
        }
        
        this.isUploading = true;
        this.hrService.uploadDocument(formData).subscribe({
          next: (response) => {
            this.isUploading = false;
            this.showDocumentModal = false;
            this.documentForm.reset();
            this.selectedDocumentFiles = [];
            this.hrService.getDocuments().subscribe();
          },
          error: (error) => {
            this.isUploading = false;
            this.documentUploadError = error.error?.message || 'Failed to upload document. Please try again.';
          }
        });
      }
    } else {
      // New document without file
      const document: Document = {
        id: 0,
        employeeId: formValue.employeeId,
        title: formValue.title,
        documentType: formValue.documentType as DocumentType,
        issueDate: new Date(formValue.issueDate),
        expiryDate: expiryDate,
        status: status,
        documentUrl: formValue.documentUrl,
        notes: formValue.notes,
        notificationsSent: []
      };
      
      this.isUploading = true;
      this.hrService.addDocument(document).subscribe({
        next: (newDoc) => {
          this.isUploading = false;
          this.showDocumentModal = false;
          this.documentForm.reset();
          this.hrService.getDocuments().subscribe();
        },
        error: (error) => {
          this.isUploading = false;
          console.error('[Document Tracking] Error adding document:', error);
          alert('Failed to add document. Please try again.');
        }
      });
    }
  }

  closeDocumentModal(): void {
    this.showDocumentModal = false;
    this.documentForm.reset();
    this.editingDocument = null;
    this.selectedDocumentFile = null;
    this.documentUploadError = '';
  }

  deleteDocument(document: Document): void {
    if (confirm('Are you sure you want to delete this document?')) {
      this.hrService.deleteDocument(document.id).subscribe(
        () => {
          if (this.editingDocument && this.editingDocument.id === document.id) {
            this.closeDocumentModal();
          }
        },
        error => console.error('Error deleting document:', error)
      );
    }
  }

  cancelEdit(): void {
    this.closeDocumentModal();
  }

  // Format date for display
  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString();
  }

  // Get class for document status (for color coding in the UI)
  getStatusClass(status: DocumentStatus): string {
    switch (status) {
      case DocumentStatus.Valid:
        return 'status-valid';
      case DocumentStatus.Expiring:
        return 'status-expiring';
      case DocumentStatus.Expired:
        return 'status-expired';
      case DocumentStatus.Missing:
        return 'status-missing';
      default:
        return '';
    }
  }

  // Get employee name by ID
  getEmployeeName(employeeId: number): Observable<string> {
    return this.employees$.pipe(
      map(employees => {
        const employee = employees.find(e => e.id === employeeId);
        return employee ? employee.name : 'Unknown Employee';
      })
    );
  }

  // Add method to open CV upload modal
  addNewCVDocument(): void {
    this.showCVUploadModal = true;
    this.cvUploadForm.reset();
    this.selectedFile = null;
    this.uploadError = '';
    
    // Add timeout to let the modal render then check form status
    setTimeout(() => {
      console.log('[Document Tracking] CV Upload Form Status:', {
        valid: this.cvUploadForm.valid,
        invalid: this.cvUploadForm.invalid,
        pristine: this.cvUploadForm.pristine,
        dirty: this.cvUploadForm.dirty,
        touched: this.cvUploadForm.touched
      });
    }, 500);
  }

  // Handle file input change
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (PDF or DOCX)
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        this.uploadError = 'Only PDF and DOCX files are allowed.';
        this.selectedFile = null;
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.uploadError = 'File size cannot exceed 10MB.';
        this.selectedFile = null;
        return;
      }
      
      this.selectedFile = file;
      this.uploadError = '';
      
      // Also mark the form as touched to trigger validation messages
      this.cvUploadForm.markAllAsTouched();
      
      // Re-validate the form now that we have a file
      this.cvUploadForm.updateValueAndValidity();
      
      // Debug log to check button conditions
      console.log('[Document Tracking] File selected:', file.name);
      console.log('[Document Tracking] Form validity after file selection:', {
        formValid: this.cvUploadForm.valid,
        formInvalid: this.cvUploadForm.invalid,
        fileSelected: !!this.selectedFile,
        isUploading: this.isUploading,
        buttonShouldBeEnabled: !(this.cvUploadForm.invalid || !this.selectedFile || this.isUploading)
      });
      
      // Log individual form controls validity
      Object.keys(this.cvUploadForm.controls).forEach(key => {
        const control = this.cvUploadForm.get(key);
        console.log(`[Document Tracking] Control '${key}' valid:`, control?.valid);
      });
    } else {
      // No file selected or file selection canceled
      this.selectedFile = null;
      console.log('[Document Tracking] No file selected or selection canceled');
    }
  }

  // Submit CV Upload - Updated with fixes
  uploadCV(): void {
    console.log('[Document Tracking] Attempting to upload CV');
    console.log('[Document Tracking] Form valid:', this.cvUploadForm.valid);
    console.log('[Document Tracking] Selected file:', !!this.selectedFile);
    
    // Check if the form is valid and a file has been selected
    if (this.cvUploadForm.invalid || !this.selectedFile) {
      // Mark all form fields as touched to show validation errors
      Object.keys(this.cvUploadForm.controls).forEach(key => {
        const control = this.cvUploadForm.get(key);
        control?.markAsTouched();
        console.log(`[Document Tracking] Control '${key}' touched and valid:`, control?.valid);
      });
      
      if (!this.selectedFile) {
        this.uploadError = 'Please select a CV file to upload.';
        console.error('[Document Tracking] No file selected');
      } else {
        this.uploadError = 'Please complete all required fields.';
        console.error('[Document Tracking] Form validation failed');
      }
      return;
    }

    // If we got here, the form is valid and a file is selected
    console.log('[Document Tracking] Form is valid and file is selected, proceeding with upload');
    this.isUploading = true;
    this.uploadError = '';

    // Get form values
    const employeeId = this.cvUploadForm.get('employeeId')?.value;
    const title = this.cvUploadForm.get('title')?.value;
    const issueDate = this.cvUploadForm.get('issueDate')?.value;

    console.log('[Document Tracking] Form values:', {
      employeeId,
      title,
      issueDate
    });

    // Create form data
    const formData = new FormData();
    formData.append('cv_file', this.selectedFile);
    formData.append('employeeId', employeeId);
    formData.append('title', title);
    
    // Format date as YYYY-MM-DD if it exists
    if (issueDate) {
      let formattedDate: string;
      
      if (issueDate instanceof Date) {
        formattedDate = issueDate.toISOString().split('T')[0];
      } else if (typeof issueDate === 'string') {
        // The date input field typically returns YYYY-MM-DD strings
        formattedDate = issueDate;
      } else {
        // If it's some other format, try to convert it
        const dateObj = new Date(issueDate);
        formattedDate = dateObj.toISOString().split('T')[0];
      }
      
      console.log('[Document Tracking] Formatted issue date:', formattedDate);
      formData.append('issueDate', formattedDate);
    }

    // Add debug log to inspect form data
    console.log('[Document Tracking] FormData field names:');
    formData.forEach((value, key) => {
      console.log(`[Document Tracking] Field: ${key}`);
    });

    // Log the form data for debugging
    console.log('[Document Tracking] Uploading CV with file:', this.selectedFile?.name);
    console.log('[Document Tracking] File size:', this.selectedFile?.size, 'bytes');
    console.log('[Document Tracking] File type:', this.selectedFile?.type);

    this.hrService.uploadCVDocument(formData).pipe(
      catchError(error => {
        console.error('[Document Tracking] CV upload error:', error);
        this.uploadError = error.error?.message || error.message || 'Failed to upload CV.';
        this.isUploading = false;
        return of(null);
      })
    ).subscribe(response => {
      this.isUploading = false;
      if (response) {
        console.log('[Document Tracking] CV uploaded successfully:', response);
        this.showCVUploadModal = false;
        this.cvUploadForm.reset();
        this.selectedFile = null;
        this.hrService.getDocuments().subscribe(); // Refresh documents
      }
    });
  }

  // View CV Summary
  viewCVSummary(document: Document): void {
    this.console.log('viewCVSummary', document);
    this.currentCVSummary = {
      id: document.id,
      title: document.title,
      status: document.cv_analysis_status || CVAnalysisStatus.Pending,
      analysisDate: document.cv_analysis_date,
      summary: 'Loading CV summary...'
    };

    this.showCVSummaryModal = true;

    if (document.cv_analysis_status === CVAnalysisStatus.Completed) {
      this.hrService.getCVAnalysis(document.id).subscribe(result => {
        if (result) {
          this.currentCVSummary = result;
        }
      });
    }
  }

  // Trigger reanalysis of a CV
  reanalyzeCVDocument(documentId: number): void {
    this.hrService.reanalyzeCVDocument(documentId).subscribe(() => {
      this.hrService.getDocuments().subscribe(); // Refresh documents
    });
  }

  // Format CV analysis status for display
  getCVStatusLabel(status: CVAnalysisStatus | undefined): string {
    if (!status) return 'Unknown';
    
    switch (status) {
      case CVAnalysisStatus.Pending: return 'Pending';
      case CVAnalysisStatus.Processing: return 'Processing';
      case CVAnalysisStatus.Completed: return 'Completed';
      case CVAnalysisStatus.Failed: return 'Failed';
      default: return 'Unknown';
    }
  }

  // Helper method to get CSS class for CV status
  getCVStatusClass(status: CVAnalysisStatus | undefined): string {
    if (!status) return 'badge bg-secondary';
    
    switch (status) {
      case CVAnalysisStatus.Pending: return 'badge bg-warning';
      case CVAnalysisStatus.Processing: return 'badge bg-info';
      case CVAnalysisStatus.Completed: return 'badge bg-success';
      case CVAnalysisStatus.Failed: return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }

  // Close CV modals
  closeCVUploadModal(): void {
    this.showCVUploadModal = false;
  }

  closeCVSummaryModal(): void {
    this.showCVSummaryModal = false;
    this.currentCVSummary = null;
  }

  // Check if document is a CV
  isCV(document: Document): boolean {
    return document.documentType.toString().toLowerCase().includes('cv');
  }

  // Force CV Upload - Bypass form validation for debugging
  forceCVUpload(): void {    
    // Check if file is selected
    if (!this.selectedFile) {
      this.uploadError = 'Please select a CV file to upload.';
      console.error('[Document Tracking] No file selected for force upload');
      return;
    }

    // Get form values, handling potential nulls
    const employeeId = this.cvUploadForm.get('employeeId')?.value || '';
    const title = this.cvUploadForm.get('title')?.value || 'Untitled CV';
    const issueDate = this.cvUploadForm.get('issueDate')?.value || new Date().toISOString().split('T')[0];

    console.log('[Document Tracking] Force upload values:', {
      employeeId,
      title,
      issueDate,
      file: this.selectedFile.name
    });

    this.isUploading = true;
    this.uploadError = '';

    // Create form data
    const formData = new FormData();
    formData.append('cv_file', this.selectedFile);
    formData.append('employeeId', employeeId);
    formData.append('title', title);
    formData.append('issueDate', issueDate);

    // Log formData
    console.log('[Document Tracking] FormData for force upload:');
    formData.forEach((value, key) => {
      console.log(`[Document Tracking] Field: ${key} = ${value instanceof File ? value.name : value}`);
    });

    // Call service
    this.hrService.uploadCVDocument(formData).pipe(
      catchError(error => {
        console.error('[Document Tracking] Force CV upload error:', error);
        this.uploadError = error.error?.message || error.message || 'Failed to upload CV.';
        this.isUploading = false;
        return of(null);
      })
    ).subscribe(response => {
      this.isUploading = false;
      if (response) {
        console.log('[Document Tracking] CV force uploaded successfully:', response);
        this.showCVUploadModal = false;
        this.cvUploadForm.reset();
        this.selectedFile = null;
        this.hrService.getDocuments().subscribe(); // Refresh documents
      }
    });
  }

  // Handle document file input change for multiple files
  onDocumentFilesSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Clear previous selections
      this.selectedDocumentFiles = [];
      this.documentUploadError = '';
      
      // Validate and add each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validate file type (PDF or DOCX)
        const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!allowedTypes.includes(file.type)) {
          this.documentUploadError = `File "${file.name}" is not allowed. Only PDF and DOCX files are accepted.`;
          continue;
        }
        
        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          this.documentUploadError = `File "${file.name}" exceeds the 10MB size limit.`;
          continue;
        }
        
        // Add valid file to selection
        this.selectedDocumentFiles.push(file);
      }
      
      console.log('[Document Tracking] Document files selected:', this.selectedDocumentFiles.length);
      this.selectedDocumentFiles.forEach(file => {
        console.log(`[Document Tracking] - ${file.name} (${file.size} bytes, ${file.type})`);
      });
    } else {
      // No files selected or file selection canceled
      this.selectedDocumentFiles = [];
      console.log('[Document Tracking] No document files selected or selection canceled');
    }
  }
  
  // Remove file from selection
  removeSelectedFile(file: File): void {
    this.selectedDocumentFiles = this.selectedDocumentFiles.filter(f => f !== file);
    console.log('[Document Tracking] Removed file from selection:', file.name);
  }

  // View document in new tab
  viewDocument(filePath: string): void {
    if (filePath) {
      window.open(filePath, '_blank');
    }
  }
}
